import { useQueryClient } from "@tanstack/react-query";
import { type ReactNode, createContext, useCallback, useContext, useEffect, useState } from "react";
import { createElement } from "react";
import { SHARED_API_URL, sharedApi, setMediaApiUrl } from "./api";

export type SiteNiche = "casino" | "news";

export type SiteConfig = {
	id: string;
	name: string;
	domain: string;
	apiUrl: string;
	niche: SiteNiche;
	color: string;
	locales: string[];
	defaultLocale: string;
};

// Bootstrap fallback used only on first paint before /sites returns.
// Picked from the four pre-spielgeil-com sites so the existing flows keep
// working if the API is briefly unreachable; the runtime list overrides
// this as soon as the fetch resolves.
const FALLBACK_SITES: SiteConfig[] = [
	{
		id: "g1-reviews",
		name: "G1 Reviews",
		domain: "g1.reviews",
		apiUrl: "https://api.g1.network/sites/g1-reviews/media-api",
		niche: "casino",
		color: "#3b82f6",
		locales: ["en", "de", "sv"],
		defaultLocale: "en",
	},
	{
		id: "spielgeil",
		name: "SpielGeil",
		domain: "spielgeil.de",
		apiUrl: "https://api.g1.network/sites/spielgeil/media-api",
		niche: "casino",
		color: "#f59e0b",
		locales: ["de"],
		defaultLocale: "de",
	},
	{
		id: "saycasinoname",
		name: "SayCasinoName",
		domain: "saycasinoname.com",
		apiUrl: "https://api.g1.network/sites/saycasinoname/media-api",
		niche: "casino",
		color: "#059669",
		locales: ["en", "de", "es"],
		defaultLocale: "en",
	},
	{
		id: "g1-news",
		name: "G1 News",
		domain: "g1.news",
		apiUrl: "https://api.g1.network/sites/g1-news/media-api",
		niche: "news",
		color: "#10b981",
		locales: ["en", "de"],
		defaultLocale: "en",
	},
];

const STORAGE_KEY = "g1_selected_site";

type SiteContextValue = {
	site: SiteConfig;
	sites: SiteConfig[];
	setSite: (site: SiteConfig) => void;
	niche: SiteNiche;
	loading: boolean;
};

const SiteContext = createContext<SiteContextValue | null>(null);

export function useSite(): SiteContextValue {
	const ctx = useContext(SiteContext);
	if (!ctx) {
		throw new Error("useSite must be used within a SiteProvider");
	}
	return ctx;
}

/**
 * Re-export so consumers (e.g. SiteSwitcher) can stop importing the
 * static SITES array directly. The actual list comes from useSite().sites
 * at runtime; this stays exported for any caller that genuinely needs a
 * pre-auth fallback.
 */
export const SITES = FALLBACK_SITES;

type ApiSite = {
	id: string;
	slug: string;
	name: string;
	domain: string;
	niche: string;
	locales: string[];
	defaultLocale: string;
	theme: Record<string, unknown>;
	status: string;
	// Authoritative hostname returned by shared-api so the admin doesn't
	// have to know about legacy aliases. Optional during the rollout window
	// when older shared-api versions don't emit it; fallback to derive.
	mediaApiHost?: string;
};

function deriveColor(theme: Record<string, unknown>): string {
	const c = theme.primaryColor;
	if (typeof c === "string" && /^#[0-9a-fA-F]{6}$/.test(c)) return c;
	return "#3b82f6";
}

function mapSite(api: ApiSite): SiteConfig {
	// Route through the api.g1.network proxy uniformly for single- and
	// multi-account sites. The browser's g1_access cookie is scoped to
	// .g1.network so it would not attach to a direct fetch against
	// `media-api.<customer-domain>`; the proxy lets shared-api validate
	// the cookie server-side and re-forward as Bearer to the per-site host.
	const sharedApiBase = SHARED_API_URL;
	return {
		id: api.slug,
		name: api.name,
		domain: api.domain,
		apiUrl: `${sharedApiBase}/sites/${api.slug}/media-api`,
		niche: api.niche === "news" ? "news" : "casino",
		color: deriveColor(api.theme ?? {}),
		locales: api.locales ?? ["en"],
		defaultLocale: api.defaultLocale ?? "en",
	};
}

function getStoredSiteId(): string | null {
	try {
		const stored = localStorage.getItem(STORAGE_KEY);
		if (!stored) return null;
		const parsed = JSON.parse(stored) as { id: string };
		return parsed.id;
	} catch {
		return null;
	}
}

function pickSite(list: SiteConfig[], preferredId: string | null): SiteConfig {
	if (preferredId) {
		const found = list.find((s) => s.id === preferredId);
		if (found) return found;
	}
	return list[0]!;
}

export function SiteProvider({ children }: { children: ReactNode }) {
	const queryClient = useQueryClient();
	const [sites, setSites] = useState<SiteConfig[]>(FALLBACK_SITES);
	const [loading, setLoading] = useState(true);
	const [site, setSiteState] = useState<SiteConfig>(() =>
		pickSite(FALLBACK_SITES, getStoredSiteId()),
	);

	const setSite = useCallback(
		(newSite: SiteConfig) => {
			setSiteState(newSite);
			localStorage.setItem(STORAGE_KEY, JSON.stringify({ id: newSite.id }));
			setMediaApiUrl(newSite.apiUrl);
			queryClient.clear();
			window.location.href = "/";
		},
		[queryClient],
	);

	// Set initial media API URL from whatever was picked on first paint.
	useEffect(() => {
		setMediaApiUrl(site.apiUrl);
		// run-once on mount; setSite handles subsequent changes
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	// Fetch the live list once after the user is authenticated. Replaces
	// the bootstrap fallback so newly-provisioned sites appear without an
	// admin redeploy.
	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const res = await sharedApi.get<{ sites: ApiSite[] }>("/sites");
				if (cancelled) return;
				const mapped = (res.sites ?? []).map(mapSite);
				if (mapped.length === 0) {
					setLoading(false);
					return;
				}
				setSites(mapped);
				const preferred = getStoredSiteId();
				const next = pickSite(mapped, preferred);
				// If the persisted selection no longer exists or the API
				// disagrees with our fallback shape, switch to the live row.
				if (next.id !== site.id || next.apiUrl !== site.apiUrl) {
					setSiteState(next);
					setMediaApiUrl(next.apiUrl);
				}
			} catch {
				// Stay on fallback; not fatal.
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const value: SiteContextValue = {
		site,
		sites,
		setSite,
		niche: site.niche,
		loading,
	};

	return createElement(SiteContext.Provider, { value }, children);
}
